import { useFormContext } from 'react-hook-form';

import useAppModal from 'hooks/useAppModal';
import { AvailableAmenity } from 'models/Amenity';
import { AvailableRule } from 'models/Rule';
import usePropertyAmenities from 'pages/property/amenities/usePropertyAmenities';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import AmenityEditModal, { amenityEditModalId } from './AmenityEditModal';
import { EditModalSchemaType } from './AmenityEditModal.schema';

const useEditAmenityModal = (amenity: AvailableAmenity | AvailableRule) => {
  const { setValue, getValues } = useFormContext();
  const { propertyAmenities } = usePropertyAmenities();
  const { openModal, closeModal } = useAppModal();
  const { property } = usePropertySettingsInitData();
  const type = 'amenityType' in amenity ? amenity.amenityType : amenity.rule;

  const amenityDescriptionName = `amenities.${type}.description`;
  const amenityPriceName = `amenities.${type}.price`;
  const amenityChargeFrequencyName = `amenities.${type}.chargeFrequency`;

  const updateAmenity = ({
    chargeFrequency,
    description,
    price,
  }: EditModalSchemaType) => {
    setValue(amenityChargeFrequencyName, chargeFrequency, {
      shouldDirty: true,
    });
    setValue(amenityDescriptionName, description, { shouldDirty: true });
    setValue(amenityPriceName, price, { shouldDirty: true });

    closeModal(amenityEditModalId);
  };

  const openAmenityEditModal = () => {
    if (!propertyAmenities || !('amenityType' in amenity)) {
      return;
    }

    const defaultAmenity = propertyAmenities[amenity.amenityType];
    const defaultValues = {
      chargeFrequency:
        getValues(amenityChargeFrequencyName) ||
        defaultAmenity?.chargeFrequency,
      description:
        getValues(amenityDescriptionName) || defaultAmenity?.description,
      price: getValues(amenityPriceName) || defaultAmenity?.price,
    };

    openModal({
      id: amenityEditModalId,
      customContent: (
        <AmenityEditModal
          amenity={amenity}
          currency={property.pricing?.currency ?? 'USD'}
          updateAmenity={updateAmenity}
          defaultValues={defaultValues}
        />
      ),
    });
  };

  return {
    openAmenityEditModal,
  };
};

export default useEditAmenityModal;
